import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Autocomplete,
  Box,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import axios from 'axios';
import { useGamesCache } from '../contexts/GamesContext';

const CreateGameModal = ({ 
  open, 
  onClose, 
  studios, // Optional: [{ StudioID, Name }, ...]
  onGameCreated, 
  initialGameName = '' 
}) => {
  const { addGameToCache, studios: cachedStudios, fetchStudios, addStudioToCache } = useGamesCache();
  const [newGameName, setNewGameName] = useState(initialGameName);
  const [useNewStudio, setUseNewStudio] = useState(false);
  const [selectedStudioID, setSelectedStudioID] = useState('');
  const [newGameStudioName, setNewGameStudioName] = useState('');
  const [newGameLiveDealer, setNewGameLiveDealer] = useState(false);
  const [newGameCrashGame, setNewGameCrashGame] = useState(false);
  const [gameMetadataJSON, setGameMetadataJSON] = useState('');
  const [creatingGame, setCreatingGame] = useState(false);
  const [createGameError, setCreateGameError] = useState('');

  const availableStudios = (studios && studios.length > 0) ? studios : cachedStudios;

  // Load studios from cache or fetch if not provided via props
  useEffect(() => {
    if (open && (!studios || studios.length === 0)) {
      fetchStudios();
    }
  }, [open, studios, fetchStudios]);

  // Reset form when modal opens or initialGameName changes
  useEffect(() => {
    if (open) {
      setNewGameName(initialGameName);
      setUseNewStudio(false);
      setSelectedStudioID('');
      setNewGameStudioName('');
      setNewGameLiveDealer(false);
      setNewGameCrashGame(false);
      setGameMetadataJSON('');
      setCreateGameError('');
      setCreatingGame(false);
    }
  }, [open, initialGameName]);

  const handleCreateGame = async () => {
    setCreateGameError('');
    if (!newGameName.trim()) {
      setCreateGameError('Game name is required.');
      return;
    }
    if (useNewStudio && !newGameStudioName.trim()) {
      setCreateGameError('Studio name is required when creating a new studio.');
      return;
    }
    if (!useNewStudio && !selectedStudioID) {
      setCreateGameError('Please select a studio.');
      return;
    }

    let parsedMetadata = null;
    if (gameMetadataJSON.trim()) {
      try {
        parsedMetadata = JSON.parse(gameMetadataJSON);
      } catch (err) {
        setCreateGameError('Metadata must be valid JSON.');
        return;
      }
    }

    setCreatingGame(true);
    try {
      const payload = {
        name: newGameName.trim(),
        live_dealer: newGameLiveDealer,
        crash_game: newGameCrashGame,
      };

      if (parsedMetadata) {
        payload.metadata = parsedMetadata;
      }

      let finalStudioID = selectedStudioID;

      if (useNewStudio) {
        try {
          const studioRes = await axios.post(
            `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/studios`,
            { Name: newGameStudioName.trim() },
            {
              headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
            }
          );
          finalStudioID = studioRes.data?.StudioID || studioRes.data?.studio_id;
        } catch (studioErr) {
          if (studioErr.response?.data?.existingStudioID) {
            finalStudioID = studioErr.response.data.existingStudioID;
          } else {
            throw studioErr;
          }
        }

        if (finalStudioID) {
          try {
            const newStudio = { StudioID: finalStudioID, Name: newGameStudioName.trim() };
            addStudioToCache(newStudio);
          } catch (e) {}
        }
      }

      payload.studio_id = finalStudioID;

      const response = await axios.post(
        `${process.env.REACT_APP_API_GAMES_STUDIOS_URL}/games`,
        payload,
        {
          headers: { 'x-api-key': process.env.REACT_APP_API_GAMES_STUDIOS_KEY },
        }
      );

      const newGameObject = {
        GameID: response.data.GameID,
        Name: payload.name,
        StudioID: finalStudioID || response.data.StudioID || 'unknown',
        CrashGame: payload.crash_game,
        LiveDealer: payload.live_dealer,
        Metadata: payload.metadata || {},
        Timestamp: new Date().toISOString()
      };

      // Best practice: Automatically inject the newly created game into the local storage cache
      try {
        addGameToCache(newGameObject);
      } catch (cacheError) {
        console.warn('Failed to surgically update local games cache', cacheError);
      }

      // Return the created game and studio info to the parent
      if (onGameCreated) {
        onGameCreated(newGameObject);
      }
      onClose();
    } catch (error) {
      console.error('Error creating game:', error);
      const message = error.response?.data?.message || error.response?.data?.error || 'Failed to create game. Please check the console.';
      setCreateGameError(message);
    } finally {
      setCreatingGame(false);
    }
  };

  return (
    <Dialog open={open} onClose={() => !creatingGame && onClose()} fullWidth maxWidth="sm">
      <DialogTitle>Create Game</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
        {createGameError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {createGameError}
          </Alert>
        )}
        <TextField
          label="Game Name"
          fullWidth
          value={newGameName}
          onChange={(e) => setNewGameName(e.target.value)}
          margin="dense"
          disabled={creatingGame}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && newGameName && (useNewStudio ? newGameStudioName : selectedStudioID)) {
                handleCreateGame();
            }
          }}
        />

        {newGameName && (
          <Box sx={{ mt: -1, mb: 1, textAlign: 'left' }}>
            <a
              href={`https://www.google.com/search?q=${encodeURIComponent(newGameName + ' slot studio')}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '0.85rem',
                color: '#1976d2',
                textDecoration: 'underline',
                cursor: 'pointer'
              }}
            >
              Search Google for Studio
            </a>
          </Box>
        )}

        <FormControlLabel
          control={
            <Switch
              checked={useNewStudio}
              onChange={(e) => setUseNewStudio(e.target.checked)}
              disabled={creatingGame}
            />
          }
          label="Create a new studio for this game"
        />

        {!useNewStudio ? (
          <Autocomplete
            options={availableStudios || []}
            getOptionLabel={(option) => option?.Name || ''}
            value={(availableStudios || []).find((studio) => studio.StudioID === selectedStudioID) || null}
            isOptionEqualToValue={(option, value) => option.StudioID === value?.StudioID}
            onChange={(event, newValue) => {
              setSelectedStudioID(newValue ? newValue.StudioID : '');
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Studio"
                variant="outlined"
                margin="dense"
                fullWidth
              />
            )}
            disabled={creatingGame}
          />
        ) : (
          <TextField
            label="New Studio Name"
            fullWidth
            value={newGameStudioName}
            onChange={(e) => setNewGameStudioName(e.target.value)}
            margin="dense"
            disabled={creatingGame}
            onKeyDown={(e) => {
                if (e.key === 'Enter' && newGameName && newGameStudioName) {
                    handleCreateGame();
                }
            }}
          />
        )}

        <FormControlLabel
          control={
            <Switch
              checked={newGameLiveDealer}
              onChange={(e) => setNewGameLiveDealer(e.target.checked)}
              disabled={creatingGame}
            />
          }
          label="Live Dealer"
        />
        <FormControlLabel
          control={
            <Switch
              checked={newGameCrashGame}
              onChange={(e) => setNewGameCrashGame(e.target.checked)}
              disabled={creatingGame}
            />
          }
          label="Crash Game"
        />

        <Accordion variant="outlined" sx={{ mt: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="body2">Advanced: Metadata</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              label="Metadata (JSON)"
              fullWidth
              value={gameMetadataJSON}
              onChange={(e) => setGameMetadataJSON(e.target.value)}
              margin="dense"
              multiline
              minRows={3}
              placeholder='{"provider":"Example"}'
              disabled={creatingGame}
            />
          </AccordionDetails>
        </Accordion>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={creatingGame}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleCreateGame}
          disabled={
            creatingGame ||
            !newGameName.trim() ||
            (!useNewStudio && !selectedStudioID) ||
            (useNewStudio && !newGameStudioName.trim())
          }
        >
          {creatingGame ? 'Creating...' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateGameModal;
